import { useMemo, useState, useEffect, useRef } from "react";
import { useDebounce } from "../../../hooks/useDebounce";
import { Button } from "../../../components/ui/button";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "../../../components/ui/resizable";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../../components/ui/tabs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ConfiguredState } from "../../../models/audit/control";
import {
  CONTROLS_ROWS_PER_PAGE_OPTIONS,
  DEFAULT_CONTROLS_ROWS_PER_PAGE,
  useControlsPage,
  useControlSessions,
  useControlFilterOptions,
} from "../../../hooks/useControlsV2";
import { ControlWithUser } from "../../../models/audit/control";
import { useCreateSessionForm } from "../../../hooks/useCreateSessionForm";
import { useWorkStepsEnabled } from "../../../hooks/useWorkStepsEnabled";
import { SetDefaultFyCycleDialog } from "../../../components/audit/SetDefaultFyCycleDialog";
import { useAuth } from "../../../contexts/authContext.tsx";
import { useOptroImport } from "../../../contexts/optroImportContext";
import { useImportingControls } from "../../../hooks/useImportingControls";
import { useOptroImportInterest } from "../../../hooks/useOptroImportInterest";
import { ControlsFilterBar } from "./components/ControlsFilterBar";
import { ControlsTable } from "./components/ControlsTable";
import { ControlDetailPanel } from "./components/ControlDetailPanel";
import { OptroAccessPanelGate } from "../../../components/audit/optroAccess/OptroAccessPanelGate";
import { OptroAccessListNotice } from "../../../components/audit/optroAccess/OptroAccessListNotice";
import { useOptroViewAccess } from "../../../hooks/useOptroViewAccess";
import { FieldworkPage } from "../fieldwork/FieldworkPage";
import { hasPermission } from "../../../lib/permissions";
import { PermissionGate } from "../../../components/PermissionGate";
import { TablePaginationBar } from "../../../components/TablePaginationBar";

const TAB_TRIGGER_CLASS =
  "rounded-none bg-transparent px-0 py-2.5 text-sm font-medium text-stone-500 shadow-none border-b-2 border-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-stone-900 data-[state=active]:text-stone-900";

export const ControlsPageV2 = () => {
  const { workspaceMember } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const workStepsEnabled = useWorkStepsEnabled();

  const [selectedTab, setSelectedTab] = useState(
    searchParams.get("tab") || "controls",
  );
  // With the work-steps flag on there is no tab strip here — Fieldwork is its
  // own page at /audit/fieldwork — so this page is always the controls view.
  // Pinning the value (rather than only hiding the strip) is what keeps every
  // `activeTab === "controls"` check below true on the flag-on path: a stale
  // `?tab=fieldwork` bookmark, made while the flag was off, would otherwise
  // land on a controls list with its header actions and detail panel switched
  // off and no tab strip left to switch back with. Flag off, this is exactly
  // `selectedTab`, so the tabbed page behaves as it always has.
  const activeTab = workStepsEnabled ? "controls" : selectedTab;

  // Initialize filters from URL query params
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || "",
  );
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [configuredStateFilters, setConfiguredStateFilters] = useState<
    ConfiguredState[]
  >(
    searchParams.getAll("configuredState").filter(Boolean) as ConfiguredState[],
  );
  const [cycleFilters, setCycleFilters] = useState<string[]>(
    searchParams.getAll("cycle").filter(Boolean),
  );
  const [processFilters, setProcessFilters] = useState<string[]>(
    searchParams.getAll("process").filter(Boolean),
  );
  const [controlOwnerFilters, setControlOwnerFilters] = useState<string[]>(
    searchParams.getAll("controlOwner").filter(Boolean),
  );
  const [sortBy, setSortBy] = useState<string>(
    searchParams.get("sortBy") || "",
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    (searchParams.get("sortOrder") as "asc" | "desc") || "desc",
  );
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [selectedControl, setSelectedControl] =
    useState<ControlWithUser | null>(null);
  const controlIdParam = searchParams.get("controlId");
  // Set when a page change closes the detail panel, until the URL drops
  // `controlId`. While set, the URL sync below leaves `controlId` out (that
  // is what drops it), and the URL → selection effect doesn't reopen the
  // panel from the old page's rows before the URL catches up.
  const droppingControlIdRef = useRef(false);
  // Seeded from the URL once, like the filters above; bad values fall back to
  // page 1 and the default size.
  const [initialPageWindow] = useState(() => {
    const page = Number(searchParams.get("page"));
    const rowsPerPage = Number(searchParams.get("pageSize"));
    return {
      page: Number.isInteger(page) && page >= 1 ? page : 1,
      rowsPerPage: CONTROLS_ROWS_PER_PAGE_OPTIONS.includes(rowsPerPage)
        ? rowsPerPage
        : DEFAULT_CONTROLS_ROWS_PER_PAGE,
    };
  });

  // Fetch filter options
  const { filterOptions } = useControlFilterOptions(false);

  // Prepare filters for the controls hook
  const filters = useMemo(
    () => ({
      searchTerm: debouncedSearchTerm || undefined,
      userIds: [],
      startDate: undefined,
      endDate: undefined,
      includeNonTemplates: false,
      configuredStates:
        configuredStateFilters.length > 0 ? configuredStateFilters : undefined,
      cycles: cycleFilters.length > 0 ? cycleFilters : undefined,
      processes: processFilters.length > 0 ? processFilters : undefined,
      controlOwners:
        controlOwnerFilters.length > 0 ? controlOwnerFilters : undefined,
      sortBy: sortBy || undefined,
      sortOrder: sortOrder,
    }),
    [
      debouncedSearchTerm,
      configuredStateFilters,
      cycleFilters,
      processFilters,
      controlOwnerFilters,
      sortBy,
      sortOrder,
    ],
  );

  const {
    controls,
    totalControlsCount,
    optroAccessUnavailable,
    loading,
    error,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    reload,
  } = useControlsPage(filters, initialPageWindow);

  // Real Optro imports land via the normal controls list fetch; each time an
  // import finishes, reload the current page so newly-created controls show
  // up.
  const { completedImportTick, registerInterest } = useOptroImport();
  useOptroImportInterest(registerInterest);
  const refreshedTickRef = useRef(completedImportTick);
  useEffect(() => {
    if (refreshedTickRef.current === completedImportTick) return;
    refreshedTickRef.current = completedImportTick;
    reload();
  }, [completedImportTick, reload]);

  // Hook for fetching sessions for the selected control
  const selectedControlId = selectedControl?.id ?? null;
  // Optro view access for the detail panel; its sessions wait for it too.
  const panelAccess = useOptroViewAccess("control", selectedControlId);
  const {
    sessions: controlSessions,
    loading: loadingSessions,
    refetch: refetchSessions,
  } = useControlSessions(
    panelAccess.state === "allowed" ? selectedControlId : null,
  );

  const {
    isOpen: isCreateSessionDialogOpen,
    handleOpenChange: handleDialogOpenChange,
    sessionName: newSessionName,
    setSessionName: setNewSessionName,
    testPhase,
    setTestPhase,
    fyCycle,
    fyCycleOptionNames,
    pendingDefaultName,
    handleFyCycleChange,
    confirmSetDefault,
    dismissSetDefault,
    loading: loadingCreateSession,
    handleCreateSession: submitCreateSession,
  } = useCreateSessionForm({ onCreated: () => refetchSessions() });

  // Mark initial load as complete
  useEffect(() => {
    if (!loading || controls.length > 0) {
      setIsInitialLoad(false);
    }
  }, [loading, controls.length]);

  // Sync controls filters to URL — only runs when on the controls tab.
  // FieldworkPage owns the URL when fieldwork tab is active.
  useEffect(() => {
    if (activeTab !== "controls") return;

    if (!controlIdParam) droppingControlIdRef.current = false;

    const params = new URLSearchParams();

    if (controlIdParam && !droppingControlIdRef.current) {
      params.set("controlId", controlIdParam);
    }

    if (searchTerm) {
      params.set("search", searchTerm);
    }

    configuredStateFilters.forEach((state) => {
      params.append("configuredState", state);
    });

    cycleFilters.forEach((cycle) => {
      params.append("cycle", cycle);
    });

    processFilters.forEach((process) => {
      params.append("process", process);
    });

    controlOwnerFilters.forEach((owner) => {
      params.append("controlOwner", owner);
    });

    if (sortBy) {
      params.set("sortBy", sortBy);
    }
    if (sortOrder) {
      params.set("sortOrder", sortOrder);
    }
    if (page > 1) {
      params.set("page", String(page));
    }
    if (rowsPerPage !== DEFAULT_CONTROLS_ROWS_PER_PAGE) {
      params.set("pageSize", String(rowsPerPage));
    }

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [
    activeTab,
    searchTerm,
    configuredStateFilters,
    cycleFilters,
    processFilters,
    controlOwnerFilters,
    sortBy,
    sortOrder,
    page,
    rowsPerPage,
    controlIdParam,
    searchParams,
    setSearchParams,
  ]);

  // Sync URL param -> selected control
  useEffect(() => {
    if (!controlIdParam) {
      if (selectedControl) {
        setSelectedControl(null);
      }
      return;
    }
    if (droppingControlIdRef.current) return;
    if (selectedControl?.id === controlIdParam) return;
    const match = controls.find((c) => c.id === controlIdParam);
    if (match) {
      setSelectedControl(match);
    }
  }, [controlIdParam, controls, selectedControl]);

  // Each page, and each new search, filter, or sort, starts at its first row.
  const tableScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    tableScrollRef.current?.scrollTo({ top: 0 });
  }, [page, rowsPerPage, filters]);

  const clearFilters = () => {
    setSearchTerm("");
    setConfiguredStateFilters([]);
    setCycleFilters([]);
    setProcessFilters([]);
    setControlOwnerFilters([]);
  };

  // `debouncedSearchTerm` is included alongside `searchTerm`: the visible
  // `controls` reflect the debounced value, so for the debounce window after
  // clearing the search the (possibly empty) result set is still filtered —
  // without this, clearing a no-match search flashed the welcome state.
  const hasActiveFilters =
    searchTerm ||
    debouncedSearchTerm ||
    configuredStateFilters.length > 0 ||
    cycleFilters.length > 0 ||
    processFilters.length > 0 ||
    controlOwnerFilters.length > 0;

  // Each control that lands mid-import reloads the list, so the table fills
  // in as the import runs instead of all at once when it finishes.
  const { visibleControls, importingControls } = useImportingControls({
    controls,
    loading,
    enabled: !hasActiveFilters,
    onItemLanded: reload,
  });

  // Mirrors ControlsTable's welcome-state condition: when the workspace has
  // no controls at all, the table shows the welcome entry cards and the
  // filter/search bar above it is hidden (nothing to filter). A first import
  // in flight shows the table instead, with a placeholder row per control.
  const showingWelcomeState =
    !loading &&
    error === null &&
    controls.length === 0 &&
    importingControls.length === 0 &&
    !hasActiveFilters;

  const activeFilterCount = [
    configuredStateFilters.length > 0,
    cycleFilters.length > 0,
    processFilters.length > 0,
    controlOwnerFilters.length > 0,
  ].filter(Boolean).length;

  const handleControlClick = (control: ControlWithUser) => {
    setSelectedControl(control);
    const next = new URLSearchParams(searchParams);
    next.set("controlId", control.id);
    setSearchParams(next);
  };

  const closeDetailPanel = () => {
    setSelectedControl(null);
    const next = new URLSearchParams(searchParams);
    next.delete("controlId");
    setSearchParams(next);
  };

  // Paging closes an open detail panel: the URL can only restore a control
  // on the page it names. With no panel open, `controlId` is a deep link
  // still waiting for its row, so it stays.
  const changePageWindow = (apply: () => void) => {
    apply();
    if (selectedControl) {
      droppingControlIdRef.current = true;
      setSelectedControl(null);
    }
  };

  const handleCreateControl = async () => {
    navigate(`/audit/controls/v2/create`);
  };

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    if (tab !== "controls") {
      setSelectedControl(null);
      // Immediately clear controls URL state and set the tab param.
      // FieldworkPage will own the URL from here.
      setSearchParams({ tab }, { replace: true });
    } else {
      // Restore controls URL state from current React state.
      const params = new URLSearchParams();
      if (searchTerm) params.set("search", searchTerm);
      configuredStateFilters.forEach((s) =>
        params.append("configuredState", s),
      );
      cycleFilters.forEach((c) => params.append("cycle", c));
      processFilters.forEach((p) => params.append("process", p));
      controlOwnerFilters.forEach((o) => params.append("controlOwner", o));
      if (sortBy) params.set("sortBy", sortBy);
      if (sortOrder) params.set("sortOrder", sortOrder);
      if (page > 1) params.set("page", String(page));
      if (rowsPerPage !== DEFAULT_CONTROLS_ROWS_PER_PAGE) {
        params.set("pageSize", String(rowsPerPage));
      }
      setSearchParams(params, { replace: true });
    }
  };

  // Show a full-page spinner only during the very first fetch on the controls tab
  if (loading && isInitialLoad && activeTab === "controls") {
    return (
      <div className="flex items-center justify-center min-h-[400px] w-full">
        <LoadingSpinner />
      </div>
    );
  }

  // The controls list itself. Shared by both branches below so the flag-on
  // layout and the flag-off Controls tab cannot drift apart.
  const controlsPane = (
    <>
      {!showingWelcomeState && (
        <div className="shrink-0 px-6 pt-4 pb-3">
          <ControlsFilterBar
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            configuredStateFilters={configuredStateFilters}
            onConfiguredStateFiltersChange={setConfiguredStateFilters}
            cycleFilters={cycleFilters}
            onCycleFiltersChange={setCycleFilters}
            processFilters={processFilters}
            onProcessFiltersChange={setProcessFilters}
            controlOwnerFilters={controlOwnerFilters}
            onControlOwnerFiltersChange={setControlOwnerFilters}
            filterOptions={filterOptions}
            isFiltersOpen={isFiltersOpen}
            onFiltersOpenChange={setIsFiltersOpen}
            activeFilterCount={activeFilterCount}
            hasActiveFilters={Boolean(hasActiveFilters)}
            onClearFilters={clearFilters}
          />
        </div>
      )}
      <OptroAccessListNotice
        unavailable={optroAccessUnavailable}
        onRetry={reload}
        className="mx-6 mb-3 w-auto shrink-0"
      />
      <div
        ref={tableScrollRef}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-auto px-6 pb-6"
      >
        {/* A failed read shows the failure, not the previous page's rows
            under the new page's range. */}
        {error !== null && !loading ? (
          <div className="border rounded-lg py-24 text-center">
            <p className="font-medium">Could not load controls.</p>
            <Button variant="outline" className="mt-4" onClick={reload}>
              Retry
            </Button>
          </div>
        ) : (
          <ControlsTable
            controls={visibleControls}
            // Placeholders show on page 1, or on a page whose rows are all still
            // importing; half-made rows stay hidden on every page.
            importingControls={
              page === 1 || visibleControls.length === 0
                ? importingControls
                : []
            }
            selectedControl={selectedControl}
            onControlClick={handleControlClick}
            hasActiveFilters={Boolean(hasActiveFilters)}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={(field) => {
              if (sortBy === field) {
                setSortOrder(sortOrder === "asc" ? "desc" : "asc");
              } else {
                setSortBy(field);
                setSortOrder("desc");
              }
            }}
            loading={loading}
          />
        )}
      </div>
      {!showingWelcomeState && (
        <div className="shrink-0 border-t border-stone-200">
          <TablePaginationBar
            page={page}
            rowsPerPage={rowsPerPage}
            total={totalControlsCount}
            onPageChange={(next) => changePageWindow(() => setPage(next))}
            onRowsPerPageChange={(next) =>
              changePageWindow(() => setRowsPerPage(next))
            }
            rowsPerPageOptions={CONTROLS_ROWS_PER_PAGE_OPTIONS}
            loading={loading}
          />
        </div>
      )}
    </>
  );

  const leftPanel = (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Page header */}
      <div className="shrink-0 px-6 pt-6 flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Controls</h1>
          <p className="text-stone-600">Manage and view your audit controls</p>
        </div>
        {activeTab === "controls" && (
          <div className="flex items-center gap-4 pb-0.5">
            {!showingWelcomeState && (
              <div className="text-sm text-stone-600">
                {totalControlsCount}{" "}
                {totalControlsCount === 1 ? "control" : "controls"}
              </div>
            )}
            <PermissionGate permission="edit_control">
              <Button onClick={handleCreateControl}>Create New Control</Button>
            </PermissionGate>
          </div>
        )}
      </div>

      {/* Tabs, flag-off only: with work-steps on, Fieldwork is its own page
          at /audit/fieldwork and this page is only the controls list. The
          wrapper below carries the layout classes that the Tabs +
          TabsContent pair carries in the tabbed branch. */}
      {workStepsEnabled ? (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden mt-4">
          {controlsPane}
        </div>
      ) : (
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="flex-1 flex flex-col min-h-0 overflow-hidden mt-4"
        >
          {/* Tab triggers */}
          <div className="shrink-0 px-6 border-b border-stone-200">
            <TabsList className="h-auto bg-transparent p-0 gap-6 rounded-none">
              <TabsTrigger value="controls" className={TAB_TRIGGER_CLASS}>
                Controls
              </TabsTrigger>
              <TabsTrigger value="fieldwork" className={TAB_TRIGGER_CLASS}>
                Fieldwork
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Controls tab */}
          <TabsContent
            value="controls"
            className="flex-1 flex flex-col min-h-0 overflow-hidden mt-0"
          >
            {controlsPane}
          </TabsContent>

          {/* Fieldwork tab */}
          <TabsContent
            value="fieldwork"
            className="flex-1 min-h-0 overflow-hidden mt-0"
          >
            {activeTab === "fieldwork" && (
              <FieldworkPage filterOptions={filterOptions} />
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );

  return (
    <>
      <ResizablePanelGroup
        autoSaveId="controls-v2-view"
        direction="horizontal"
        className="w-full max-w-[2500px] mx-auto"
      >
        <ResizablePanel
          id="left-panel"
          order={1}
          defaultSize={100}
          minSize={30}
        >
          <div className="h-screen">{leftPanel}</div>
        </ResizablePanel>

        {selectedControl && activeTab === "controls" && (
          <>
            <ResizableHandle />
            <ResizablePanel
              id="right-panel"
              order={2}
              defaultSize={40}
              minSize={10}
            >
              <OptroAccessPanelGate
                kind="control"
                access={panelAccess}
                onClose={closeDetailPanel}
              >
                <ControlDetailPanel
                  control={selectedControl}
                  sessions={controlSessions}
                  loadingSessions={loadingSessions}
                  onClose={closeDetailPanel}
                  isCreateSessionDialogOpen={isCreateSessionDialogOpen}
                  onCreateSessionDialogOpenChange={handleDialogOpenChange}
                  newSessionName={newSessionName}
                  onNewSessionNameChange={setNewSessionName}
                  testPhase={testPhase}
                  onTestPhaseChange={setTestPhase}
                  fyCycle={fyCycle}
                  onFyCycleChange={handleFyCycleChange}
                  fyCycleOptions={fyCycleOptionNames}
                  onCreateSession={(optroTestId) => {
                    if (selectedControl) {
                      void submitCreateSession(selectedControl.id, optroTestId);
                    }
                  }}
                  loadingCreateSession={loadingCreateSession}
                  canEditControl={hasPermission(
                    workspaceMember,
                    "edit_control",
                  )}
                  canEditTest={hasPermission(workspaceMember, "edit_test")}
                  onSessionDeleted={refetchSessions}
                />
              </OptroAccessPanelGate>
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>

      <SetDefaultFyCycleDialog
        open={pendingDefaultName !== null}
        optionName={pendingDefaultName ?? ""}
        onConfirm={confirmSetDefault}
        onDismiss={dismissSetDefault}
      />
    </>
  );
};
